import React from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import type { PharmacySale } from '../../types/pharmacy-pos';

interface PrintTemplateA4Props {
  sale: PharmacySale;
  hospitalName?: string;
  hospitalAddress?: string;
  hospitalPhone?: string;
  hospitalEmail?: string;
  logoUrl?: string;
}

export const PrintTemplateA4: React.FC<PrintTemplateA4Props> = ({
  sale,
  hospitalName = 'Medical Center',
  hospitalAddress = 'Hospital Address, City, Country',
  hospitalPhone = 'Phone: +880-XXX-XXXXXX',
  hospitalEmail = 'Email: info@hospital.com',
  logoUrl
}) => {
  const { formatCurrency } = useCurrency();
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="print-template-a4" style={{ width: '210mm', minHeight: '297mm', padding: '20mm', fontFamily: 'Arial, sans-serif' }}>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-template-a4, .print-template-a4 * {
            visibility: visible;
          }
          .print-template-a4 {
            position: absolute;
            left: 0;
            top: 0;
            width: 210mm;
            padding: 20mm;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: '3px solid #0f3d2e', paddingBottom: '15px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {logoUrl && (
              <img src={logoUrl} alt="Logo" style={{ height: '60px', marginBottom: '10px' }} />
            )}
            <h1 style={{ margin: '0 0 5px 0', color: '#0f3d2e', fontSize: '28px' }}>{hospitalName}</h1>
            <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>{hospitalAddress}</p>
            <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>{hospitalPhone} | {hospitalEmail}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h2 style={{ margin: '0 0 10px 0', color: '#0f3d2e', fontSize: '24px' }}>PHARMACY INVOICE</h2>
            <div style={{ fontSize: '14px', color: '#666' }}>
              <div><strong>Invoice No:</strong> {sale.invoice_no}</div>
              <div><strong>Date:</strong> {formatDate(sale.created_at)}</div>
              <div><strong>Type:</strong> <span style={{ textTransform: 'uppercase', color: '#0f3d2e', fontWeight: 'bold' }}>{sale.sale_type}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Patient/Customer Information */}
      {(sale.patient_name || sale.patient_uhid) && (
        <div style={{ backgroundColor: '#e9f3ef', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#0f3d2e', fontSize: '16px' }}>
            {sale.sale_type === 'ipd' ? 'IPD Patient Information' : 
             sale.sale_type === 'opd' ? 'OPD Patient Information' : 
             'Customer Information'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
            {sale.patient_uhid && (
              <div><strong>UHID:</strong> {sale.patient_uhid}</div>
            )}
            {sale.patient_name && (
              <div><strong>Name:</strong> {sale.patient_name}</div>
            )}
            {sale.patient_phone && (
              <div><strong>Phone:</strong> {sale.patient_phone}</div>
            )}
            {sale.patient_address && (
              <div><strong>Address:</strong> {sale.patient_address}</div>
            )}
            {sale.admission_id && (
              <div><strong>Admission No:</strong> {sale.admission_id}</div>
            )}
            {sale.bed_id && (
              <div><strong>Bed No:</strong> {sale.bed_id}</div>
            )}
            {sale.doctor_id && (
              <div><strong>Doctor ID:</strong> {sale.doctor_id}</div>
            )}
            {sale.prescription_no && (
              <div><strong>Prescription No:</strong> {sale.prescription_no}</div>
            )}
          </div>
        </div>
      )}

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr style={{ backgroundColor: '#0f3d2e', color: 'white' }}>
            <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #0f3d2e' }}>SL</th>
            <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #0f3d2e' }}>Medicine Name</th>
            <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #0f3d2e' }}>Batch</th>
            <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #0f3d2e' }}>Qty</th>
            <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #0f3d2e' }}>Unit Price</th>
            <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #0f3d2e' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {sale.items?.map((item, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '10px', border: '1px solid #ddd' }}>{index + 1}</td>
              <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                <div style={{ fontWeight: 'bold' }}>{item.medicine_name}</div>
                {item.generic_name && (
                  <div style={{ fontSize: '12px', color: '#666' }}>({item.generic_name})</div>
                )}
              </td>
              <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd', fontSize: '12px' }}>
                {item.batch_no || 'N/A'}
              </td>
              <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd' }}>{item.quantity}</td>
              <td style={{ padding: '10px', textAlign: 'right', border: '1px solid #ddd' }}>formatCurrency(item.unit_price)</td>
              <td style={{ padding: '10px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold' }}>formatCurrency(item.line_total)</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals Section */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
        <div style={{ width: '300px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
            <span>Subtotal:</span>
            <span>formatCurrency(sale.subtotal)</span>
          </div>
          {sale.discount_amount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd', color: '#d32f2f' }}>
              <span>Discount {sale.discount_mode === 'percent' ? `(${sale.discount_value}%)` : ''}:</span>
              <span>-formatCurrency(sale.discount_amount)</span>
            </div>
          )}
          {sale.vat_amount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
              <span>VAT ({sale.vat_percent}%):</span>
              <span>formatCurrency(sale.vat_amount)</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid #0f3d2e', marginTop: '5px', fontSize: '18px', fontWeight: 'bold', color: '#0f3d2e' }}>
            <span>GRAND TOTAL:</span>
            <span>formatCurrency(sale.grand_total)</span>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      {sale.payment_method && (
        <div style={{ backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Payment Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', fontSize: '14px' }}>
            <div><strong>Payment Mode:</strong> <span style={{ textTransform: 'uppercase' }}>{sale.payment_method}</span></div>
            {sale.amount_received > 0 && (
              <div><strong>Amount Received:</strong> formatCurrency(sale.amount_received)</div>
            )}
            {sale.change_due > 0 && (
              <div><strong>Change Due:</strong> formatCurrency(sale.change_due)</div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ borderTop: '2px solid #0f3d2e', paddingTop: '15px', marginTop: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
          <div>
            <p style={{ margin: '0 0 5px 0' }}><strong>Terms & Conditions:</strong></p>
            <ul style={{ margin: '0', paddingLeft: '20px' }}>
              <li>Please check all medicines before leaving the counter</li>
              <li>No refund or exchange without proper authorization</li>
              <li>Keep this invoice for future reference</li>
            </ul>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: '0 0 5px 0' }}>Authorized Signature</p>
            <div style={{ borderTop: '1px solid #000', width: '150px', marginTop: '30px' }}></div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '11px', color: '#999' }}>
          Printed on: {new Date().toLocaleString('en-GB')} | System Generated Invoice
        </div>
      </div>
    </div>
  );
};
